import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, FileText, Zap, ClipboardCheck, CheckCircle, Circle, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const SigmaHQ = () => {
  const [sigmaData, setSigmaData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetch('/content/sigma-hq.json')
      .then(res => res.json())
      .then(data => setSigmaData(data))
      .catch(err => console.error('Error loading Sigma HQ data:', err));
  }, []);

  const featureIcons = {
    'search': Search,
    'file-text': FileText,
    'zap': Zap,
    'clipboard-check': ClipboardCheck
  };

  if (!sigmaData) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Back Navigation */}
      <div className="py-6 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <Button variant="ghost" asChild>
            <Link to="/" className="flex items-center space-x-2">
              <ArrowLeft size={16} />
              <span>Back to Portfolio</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>

        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
                {sigmaData.status}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {sigmaData.title}
              </h1>
              <p className="text-xl text-blue-400 mb-6">
                {sigmaData.tagline}
              </p>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                {sigmaData.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                  <div className="text-2xl font-bold text-white">{sigmaData.stats.projects}</div>
                  <div className="text-sm text-slate-400">Active Projects</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                  <div className="text-2xl font-bold text-white">{sigmaData.stats.offices}</div>
                  <div className="text-sm text-slate-400">Offices</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                  <div className="text-2xl font-bold text-white">{sigmaData.stats.status}</div>
                  <div className="text-sm text-slate-400">Status</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-700">
                <img
                  src={sigmaData.screenshots[0].src}
                  alt={sigmaData.screenshots[0].alt}
                  className="w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                  onClick={() => setSelectedImage(sigmaData.screenshots[0])}
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">AI-Powered Features</h2>
            <p className="text-lg text-gray-600">Built to solve real problems in construction project management</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {sigmaData.features.map((feature, index) => {
              const IconComponent = featureIcons[feature.icon] || Zap;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 border-0 shadow-md hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">{feature.name}</h3>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Screenshot Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">See It In Action</h2>
            <p className="text-lg text-gray-600">Screenshots from the live production system</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {sigmaData.screenshots.slice(1).map((screenshot, index) => (
              <Card
                key={index}
                className="overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                onClick={() => setSelectedImage(screenshot)}
              >
                <div className="relative">
                  <img
                    src={screenshot.src}
                    alt={screenshot.alt}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p className="text-white p-4 text-sm">{screenshot.caption}</p>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600">{screenshot.caption}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Development Roadmap</h2>
            <p className="text-lg text-gray-600">Continuous evolution to meet real-world needs</p>
          </div>
          <div className="space-y-4">
            {sigmaData.roadmap.map((phase, index) => (
              <div
                key={index}
                className={`flex items-start gap-4 p-4 rounded-lg ${
                  phase.status === 'complete' ? 'bg-green-50 border border-green-200' :
                  phase.status === 'in-progress' ? 'bg-blue-50 border border-blue-200' :
                  'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className="flex-shrink-0 mt-1">
                  {phase.status === 'complete' ? (
                    <CheckCircle className="text-green-600" size={24} />
                  ) : phase.status === 'in-progress' ? (
                    <div className="w-6 h-6 rounded-full border-2 border-blue-600 border-t-transparent animate-spin"></div>
                  ) : (
                    <Circle className="text-gray-400" size={24} />
                  )}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{phase.phase}</h3>
                    <Badge
                      variant="outline"
                      className={
                        phase.status === 'complete' ? 'border-green-500 text-green-700' :
                        phase.status === 'in-progress' ? 'border-blue-500 text-blue-700' :
                        'border-gray-400 text-gray-600'
                      }
                    >
                      {phase.status === 'complete' ? 'Complete' :
                       phase.status === 'in-progress' ? 'In Progress' : 'Planned'}
                    </Badge>
                  </div>
                  <p className="text-gray-600 text-sm">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Built With</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {sigmaData.technologies.map((tech, index) => (
              <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Want to See It In Action?</h2>
          <p className="text-slate-300 text-lg mb-8">
            Sigma HQ is a custom-built solution, but the approach can be adapted to any industry.
            Let's talk about how AI can transform your operations.
          </p>
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Link to="/contact" className="flex items-center gap-2">
              Get In Touch
              <ArrowRight size={18} />
            </Link>
          </Button>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded">
            {selectedImage.caption}
          </p>
        </div>
      )}
    </div>
  );
};

export default SigmaHQ;
